import { ReactComponent as LockIcon } from "svg/lock-icon.svg";
import { Item } from "./BuildPageContainer";
import { getDeadlockImagePath } from "lib/general-helper";
import { TooltipContainer } from "@outplayed/tooltips";
import classNames from "classnames";
import ItemTooltip from "components/ItemTooltip";

interface BuildStageProps {
  baseItems: Item[];
  optionalEarlyItems?: Item[];
  sellItems?: Item[];
  optionalLateItems?: Item[];
}

const BuildStage = (props: BuildStageProps) => {
  const { baseItems } = props;

  const orangeSlots: (Item | null)[] = [];
  const greenSlots: (Item | null)[] = [];
  const purpleSlots: (Item | null)[] = [];
  const flexSlots: (Item | null)[] = [];

  baseItems.forEach((item) => {
    if (item.type === "EItemSlotType_WeaponMod") {
      if (orangeSlots.length < 4) {
        orangeSlots.push(item);
      } else if (flexSlots.length < 4) {
        flexSlots.push(item);
      }
    }
    if (item.type === "EItemSlotType_Armor") {
      if (greenSlots.length < 4) {
        greenSlots.push(item);
      } else if (flexSlots.length < 4) {
        flexSlots.push(item);
      }
    }
    if (item.type === "EItemSlotType_Tech") {
      if (purpleSlots.length < 4) {
        purpleSlots.push(item);
      } else if (flexSlots.length < 4) {
        flexSlots.push(item);
      }
    }
  });

  if (orangeSlots.length < 4) {
    const remainingSlots = 4 - orangeSlots.length;
    for (let i = 0; i < remainingSlots; i++) {
      orangeSlots.push(null);
    }
  }
  if (greenSlots.length < 4) {
    const remainingSlots = 4 - greenSlots.length;
    for (let i = 0; i < remainingSlots; i++) {
      greenSlots.push(null);
    }
  }
  if (purpleSlots.length < 4) {
    const remainingSlots = 4 - purpleSlots.length;
    for (let i = 0; i < remainingSlots; i++) {
      purpleSlots.push(null);
    }
  }
  if (flexSlots.length < 4) {
    const remainingSlots = 4 - flexSlots.length;
    for (let i = 0; i < remainingSlots; i++) {
      flexSlots.push(null);
    }
  }

  const getItemTier = (itemCost: number) => {
    if (itemCost <= 500) return "I";
    if (itemCost <= 1250) return "II";
    if (itemCost <= 3000) return "III";
    return "IV";
  };

  return (
    <div className="build-stage-wrapper">
      <div className="build-stage-container">
        <div className="item-type-containers-wrapper">
          <div className="item-type-container orange">
            <img src="https://static.bigbrain.gg/assets/deadlock/icon-weapon.png" alt="Courage" className="keystat-icon" />
            {orangeSlots.map((slot, index) => {
              const iconImage = getDeadlockImagePath(slot?.iconPath ?? "");

              return (
                <div className="item-slot-container orange" key={index}>
                  {slot !== null && (
                    <TooltipContainer tooltipInfo={<ItemTooltip item={slot} />}>
                      <div className="item-slot orange">
                        <img
                          src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`}
                          alt={slot.name}
                        />
                        <div className="item-tier orange">{getItemTier(slot.cost)}</div>
                      </div>
                    </TooltipContainer>
                  )}
                </div>
              );
            })}
          </div>
          <div className="item-type-container green">
            <img src="https://static.bigbrain.gg/assets/deadlock/icon-vitality.png" alt="Fortitude" className="keystat-icon" />
            {greenSlots.map((slot, index) => {
              const iconImage = getDeadlockImagePath(slot?.iconPath ?? "");
              return (
                <div className="item-slot-container green" key={index}>
                  {slot !== null && (
                    <TooltipContainer tooltipInfo={<ItemTooltip item={slot} />}>
                      <div className="item-slot green">
                        <img
                          src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`}
                          alt={slot.name}
                        />
                        <div className="item-tier green">{getItemTier(slot.cost)}</div>
                      </div>
                    </TooltipContainer>
                  )}
                </div>
              );
            })}
          </div>
          <div className="item-type-container purple">
            <img src="https://static.bigbrain.gg/assets/deadlock/icon-spirit.png" alt="Spirit" className="keystat-icon" />
            {purpleSlots.map((slot, index) => {
              const iconImage = getDeadlockImagePath(slot?.iconPath ?? "");
              return (
                <div className="item-slot-container purple" key={index}>
                  {slot !== null && (
                    <TooltipContainer tooltipInfo={<ItemTooltip item={slot} />}>
                      <div className="item-slot purple">
                        <img
                          src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`}
                          alt={slot.name}
                        />
                        <div className="item-tier purple">{getItemTier(slot.cost)}</div>
                      </div>
                    </TooltipContainer>
                  )}
                </div>
              );
            })}
          </div>
          <div className="item-type-container flex">
            {flexSlots.map((slot, index) => {
              const iconImage = getDeadlockImagePath(slot?.iconPath ?? "");
              return (
                <div className="item-slot-container flex" key={index}>
                  {slot === null && <LockIcon className="lock-icon" />}
                  {slot !== null && (
                    <TooltipContainer tooltipInfo={<ItemTooltip item={slot} />}>
                      <div
                        className={classNames("item-slot", {
                          orange: slot.type === "EItemSlotType_WeaponMod",
                          green: slot.type === "EItemSlotType_Armor",
                          purple: slot.type === "EItemSlotType_Tech",
                        })}
                      >
                        <img
                          src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`}
                          alt={slot.name}
                        />
                        <div
                          className={classNames("item-tier", {
                            orange: slot.type === "EItemSlotType_WeaponMod",
                            green: slot.type === "EItemSlotType_Armor",
                            purple: slot.type === "EItemSlotType_Tech",
                          })}
                        >
                          {getItemTier(slot.cost)}
                        </div>
                      </div>
                    </TooltipContainer>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildStage;
